import { UiCombinedNomenclature } from "@/generated/ui-api";

export const getEffectiveLength = (code: string): number => {
  if (code.length !== 8) return 0;
  if (code.substring(6, 8) !== "00") return 8;
  if (code.substring(4, 8) !== "0000") return 6;
  if (code.substring(2, 8) !== "000000") return 4;
  return 2;
};

export const formatCode = (code: string): string => {
  const len = getEffectiveLength(code);
  return code.substring(0, len);
};

export const getParentCode = (code: string | null): string | null => {
  if (!code || code.length !== 8) return null;

  if (code.endsWith("000000")) return null;
  if (code.endsWith("0000")) return `${code.substring(0, 2)}000000`;
  if (code.endsWith("00")) return `${code.substring(0, 4)}0000`;
  return `${code.substring(0, 6)}00`;
};

export const getPathToCode = (
  code: string | null,
  allItems: UiCombinedNomenclature[]
): UiCombinedNomenclature[] => {
  if (!code) return [];
  const allCodes = new Set(allItems.map(i => i.code));
  const path: UiCombinedNomenclature[] = [];
  let currentCode: string | null = code;

  while (currentCode) {
    const item = allItems.find(i => i.code === currentCode);
    if (item) {
      path.unshift(item);

      let ancestorCode = getParentCode(currentCode);
      while (ancestorCode && !allCodes.has(ancestorCode)) {
        ancestorCode = getParentCode(ancestorCode);
      }
      currentCode = ancestorCode;
    } else {
      break;
    }
  }
  return path;
};

export const getDirectChildren = (
  parentCode: string | null,
  allItems: UiCombinedNomenclature[]
): UiCombinedNomenclature[] => {
  const allCodes = new Set(allItems.map(i => i.code));

  return allItems.filter(item => {
    if (item.code === parentCode) return false;

    if (parentCode !== null) {
      const parentLen = getEffectiveLength(parentCode);
      const prefix = parentCode.substring(0, parentLen);
      if (!item.code.startsWith(prefix)) return false;
    }

    let ancestorCode = getParentCode(item.code);
    while (ancestorCode && !allCodes.has(ancestorCode)) {
      ancestorCode = getParentCode(ancestorCode);
    }

    return ancestorCode === parentCode;
  });
};

export const getAllDescendants = (
  parentCode: string | null,
  allItems: UiCombinedNomenclature[]
): UiCombinedNomenclature[] => {
  if (parentCode === null) {
    return allItems;
  }

  const parentLen = getEffectiveLength(parentCode);
  const prefix = parentCode.substring(0, parentLen);

  return allItems.filter(
    item => item.code.startsWith(prefix) && item.code !== parentCode
  );
};
